import { getSupabase } from '@/lib/supabase'
import { withAuthRecovery } from '@/lib/authRecovery'
import type {
  McUserForPedagogyAdmin,
  PedagogySpecialistListItem,
  PedagogySpecialistPartner,
  PedagogySpecialistPartnerCoverageRow,
} from '@/types'

function formatError(error: unknown): string {
  if (!error) return 'Unknown error'
  if (error instanceof Error) return error.message
  if (typeof error === 'object') {
    const e = error as { message?: string; details?: string; hint?: string; code?: string }
    return [e.message, e.details, e.hint, e.code].filter(Boolean).join(' | ') || JSON.stringify(error)
  }
  return String(error)
}

export async function listPedagogySpecialists(): Promise<PedagogySpecialistListItem[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('list_pedagogy_specialists')
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as unknown as PedagogySpecialistListItem[]
}

export async function listMcUsersForPedagogyAdmin(): Promise<McUserForPedagogyAdmin[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('list_mc_users_for_pedagogy_admin')
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as unknown as McUserForPedagogyAdmin[]
}

export async function setUserPedagogySpecialistRole(params: {
  userId: string
  isSpecialist: boolean
  effectiveAt?: string | null
  note?: string | null
}): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    supabase.rpc('set_user_pedagogy_specialist_role', {
      p_user_id: params.userId,
      p_is_specialist: params.isSpecialist,
      p_effective_at: params.effectiveAt ?? null,
      p_note: params.note ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
}

export async function setUserPedagogyLeadRole(params: {
  userId: string
  isLead: boolean
  note?: string | null
}): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    supabase.rpc('set_user_pedagogy_lead_role', {
      p_user_id: params.userId,
      p_is_lead: params.isLead,
      p_note: params.note ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
}

export async function cancelScheduledPedagogyRemoval(userId: string): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    supabase.rpc('cancel_scheduled_pedagogy_removal', { p_user_id: userId })
  )
  if (error) throw new Error(formatError(error))
}

export async function getPedagogySpecialistPartners(
  specialistUserId: string,
  periodId?: number | null
): Promise<PedagogySpecialistPartner[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('get_pedagogy_specialist_partners', {
      p_specialist_user_id: specialistUserId,
      p_period_id: periodId ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as unknown as PedagogySpecialistPartner[]
}

export async function listPedagogySpecialistPartnerCoverage(): Promise<PedagogySpecialistPartnerCoverageRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('list_pedagogy_specialist_partner_coverage_v2')
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as unknown as PedagogySpecialistPartnerCoverageRow[]
}

export async function assignPedagogySpecialist(params: {
  specialistUserId: string
  partnerNumber: number
  assignedFrom?: string | null
  note?: string | null
}): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('assign_pedagogy_specialist', {
      p_specialist_user_id: params.specialistUserId,
      p_partner_number: params.partnerNumber,
      p_assigned_from: params.assignedFrom ?? null,
      p_note: params.note ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
  return data as number
}

export async function transferPedagogySpecialist(params: {
  partnerNumber: number
  newSpecialistUserId: string
  effectiveFrom?: string | null
  note?: string | null
}): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('transfer_pedagogy_specialist', {
      p_partner_number: params.partnerNumber,
      p_new_specialist_user_id: params.newSpecialistUserId,
      p_effective_from: params.effectiveFrom ?? null,
      p_note: params.note ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
  return data as number
}

export async function updatePedagogySpecialistAssignmentStart(params: {
  assignmentId: number
  assignedFrom: string
  note?: string | null
}): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    supabase.rpc('update_pedagogy_specialist_assignment_start', {
      p_assignment_id: params.assignmentId,
      p_assigned_from: params.assignedFrom,
      p_note: params.note ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
}

export async function unassignPedagogySpecialist(params: {
  assignmentId: number
  unassignedAt?: string | null
  note?: string | null
}): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    supabase.rpc('unassign_pedagogy_specialist', {
      p_assignment_id: params.assignmentId,
      p_unassigned_at: params.unassignedAt ?? null,
      p_note: params.note ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
}

export async function cancelScheduledPedagogyAssignmentClose(assignmentId: number): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    supabase.rpc('cancel_scheduled_pedagogy_assignment_close', {
      p_assignment_id: assignmentId,
    })
  )
  if (error) throw new Error(formatError(error))
}
